/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {CheckListEntityFinancials, RestResponse} from '../model';
import {environment} from '../../environments/environment';
import {ApiHeaders} from './headers';
import {SearchableProvider} from '../model/interface/searchable.provider';
import { CustomDocuments } from '../model/custom.documents';

@Injectable()
export class ChecklistProvider implements SearchableProvider {

  constructor(
    private http: HttpClient
  ) {
  }

  setField(id: string, field: string, value: any): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/checklist/set`, {
        FIELD: field,
        VALUE: value
      }, ApiHeaders.getOptions());
  }

  setDataField(id: string, did: string, field: string, value: any): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/checklist/data/${did}/set`, {
        FIELD: field,
        VALUE: value
      }, ApiHeaders.getOptions());
  }

  setDataFinField(id: string, did: string, fid: string, field: string, value: any): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/checklist/data/${did}/${fid}/set`, {
        FIELD: field,
        VALUE: value
      }, ApiHeaders.getOptions());
  }

  setData(id: string, did: string, data: CheckListEntityFinancials[], intDate: string, fyeDate: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/checklist/data/${did}`, {
        DATA: data,
        DATE: intDate,
        FYE_DATE: fyeDate
      }, ApiHeaders.getOptions());
  }

  setDate(id: string, details_id: string, data: CheckListEntityFinancials[], intDate: string, fyeDate: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/checklist/details/${details_id}/data`, {
        DATA: data,
        DATE: intDate,
        FYE_DATE: fyeDate
      }, ApiHeaders.getOptions());
  }

  changeDate(id: string, details_id: string, data: CheckListEntityFinancials[], intDate: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/checklist/details/${details_id}/change`, {
        DATA: data,
        DATE: intDate
      }, ApiHeaders.getOptions());
  }

  copyDate(id: string, details_id: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/checklist/details/${details_id}/copy`, {}, ApiHeaders.getOptions());
  }

  setDataFullField(id: string, did: string, field: string, value: any): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/checklist/data/${did}/set/all`, {
        FIELD: field,
        VALUE: value
      }, ApiHeaders.getOptions());
  }

  addCustomDocument(id: string): Observable<RestResponse> {
    return this.http
      .post<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/checklist/document`, {}, ApiHeaders.getOptions());
  }

  updateDocument(id: string, document: CustomDocuments): Observable<RestResponse> {
    return this.http
      .put<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/checklist/document/${document.ID}/field`,
        document, ApiHeaders.getOptions());
  }

  deleteDocument(id: string, did: string): Observable<RestResponse> {
    return this.http
      .delete<RestResponse>(`${environment.API_URL}/api/pma/report/${id}/checklist/document/${did}`, ApiHeaders.getOptions());
  }

  getSearchType(): string {
    return 'REPORT_CHECKLIST';
  }

  save(id: string, field: string, value: any): Observable<RestResponse> {
    return this.setField(id, field, value);
  }

  search(query?: string, id?: string, nonBank?: boolean, lsp?: boolean, page?: number): Observable<RestResponse> {
    return undefined;
  }
}
